import { queryOptions, useQuery } from '@tanstack/react-query'
import { minutesToMilliseconds } from 'date-fns/minutesToMilliseconds'
import { fetchTimeslots } from '@/features/shop/services/DeliveryWindows/fetchers'
import {
  type TimeslotRequestParams,
  type TimeslotsApiVersion,
} from '@/features/shop/services/DeliveryWindows/types'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import {
  useIsPickup,
  useUserDefaultShoppingAddressId,
  useIsGuestUser,
  useZipForZipOnlySignedInUser,
} from '@/features/account/services/User/hooks'
import { useQueryCart } from '@/features/shop/services/Cart/queries'
import { useDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'
import { useGuestParams } from '@/features/account/services/GuestUser/hooks'
import { getRetailerETAQuery } from '@/features/shop/services/DeliveryWindows/utils'
import { retailerETAQueryOptions } from '@/features/shop/services/DeliveryWindows/queryOptions'

export const TimeslotsRootKey = 'time slots'

const getTimeslotsQueryKey = (params: TimeslotRequestParams, apiVersion: TimeslotsApiVersion) =>
  [TimeslotsRootKey, { ...params, apiVersion }] as const
export type TimeslotsQueryKey = ReturnType<typeof getTimeslotsQueryKey>

export const timeslotsQueryOptions = (
  params: TimeslotRequestParams,
  apiVersion: TimeslotsApiVersion = 'v2',
) =>
  queryOptions({
    queryKey: getTimeslotsQueryKey(params, apiVersion),
    queryFn: fetchTimeslots,
    staleTime: minutesToMilliseconds(10),
  })

export const useQueryTimeSlots = ({
  params,
  enabled = true,
  keepPreviousData = false,
  apiVersion = 'v2',
}: {
  params: Omit<TimeslotRequestParams, 'store_location_id'>
  enabled?: boolean
  keepPreviousData?: boolean
  apiVersion?: TimeslotsApiVersion
}) => {
  const { store_location_id } = useStoreParams()

  return useQuery({
    ...timeslotsQueryOptions(
      {
        ...params,
        store_location_id: store_location_id ?? 0,
      },
      apiVersion,
    ),
    enabled: Boolean(store_location_id && enabled),
    placeholderData: (previousData) => (keepPreviousData ? previousData : undefined),
  })
}

export const useQueryTimeSlotsForCart = (
  context: TimeslotRequestParams['context'],
  keepPreviousData = false,
) => {
  const isPickup = useIsPickup()
  const address = useDefaultShoppingAddress()
  const { data: cart, isFetched: isCartFetched } = useQueryCart()
  const userAddressId = useUserDefaultShoppingAddressId()

  return useQueryTimeSlots({
    params: {
      address_id: address.id ?? 0,
      address_timezone: address.timezone,
      has_alcohol: Boolean(cart?.is_alcohol),
      item_count: cart?.shopping_cart_lines.length ?? 0,
      is_pickup: isPickup,
      context,
    },
    enabled: Boolean(userAddressId && isCartFetched),
    keepPreviousData,
  })
}

export const useQueryRetailerETA = () => {
  const { store_location_id } = useStoreParams()
  const address = useDefaultShoppingAddress()
  const userAddressId = useUserDefaultShoppingAddressId()
  const isGuestUser = useIsGuestUser()
  const zipForZipOnlyUser = useZipForZipOnlySignedInUser()
  const { guestAddress } = useGuestParams() ?? {}

  const { params, enabled } = getRetailerETAQuery({
    storeLocationId: store_location_id,
    userAddressId,
    address,
    guestAddress,
    zipForZipOnlyUser,
    isGuestUser,
  })
  return useQuery({ ...retailerETAQueryOptions(params), enabled })
}
